import { Box, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { PopperContent } from '@/components/poppers/PopperContent';
import { HeaderCellTypography } from '@/components/tables/components/HeaderCellTypography';
import { DataTable_LEGACY } from '@/components/tables/DataTable_LEGACY/DataTable_LEGACY';
import { Column } from '@/components/tables/DataTable_LEGACY/tableTypes';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { NUMBER_PREVIEW_ASSETS } from '@/modules/assetProviderIntegrations/shared/constants';
import { ExternalIntegrationButton } from '@/modules/assetProviderIntegrations/shared/ExternalIntegrationButton';
import { AssetIntegration_IntegrationEntityFragment } from '@/modules/assetProviderIntegrations/shared/graphql/assetProviderIntegration.fragments.generated';
import { AssetV2Fragment } from '@/modules/assets/graphql/Asset.generated';
import { getAssetCellDefinition } from '@/modules/assets/utils';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { SharePriceContent } from '@/modules/content/tooltipContent/SharePriceContent';
import { IntegratedAssetTaxSitusCell } from '@/modules/entities/details/shared/IntegratedAssetTaxSitusCell';
import { STATE_TAX_SITUS_HELP_BODY } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.fields';
import { AssetTaxSitusFormValue } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import { COLORS } from '@/styles/tokens/colors';
import { AssetValueV2OwnershipType } from '@/types/schema';
import { formatCurrency } from '@/utils/formatting/currency';
import { commaDecimal } from '@/utils/formatting/numbers';

export enum AssetValuationTableColumns {
  DISPLAY_NAME = 'displayName',
  SHARE_COUNT = 'shareCount',
  SHARE_VALUE = 'shareValue',
  TAX_SITUS = 'taxSitus',
  TOTAL_MARKET_VALUE = 'totalMarketValue',
}

export type AssetV2Extended = AssetV2Fragment & {
  accountName?: string;
  displayCategory: string;
};

const columns: Readonly<Column[]> = [
  {
    field: AssetValuationTableColumns.DISPLAY_NAME,
    headerName: 'Asset',
    flex: 1,
    sortable: false,
  },
  {
    field: AssetValuationTableColumns.SHARE_COUNT,
    headerName: 'Share count',
    width: 110,
    align: 'right',
    sortable: false,
  },
  {
    field: AssetValuationTableColumns.SHARE_VALUE,
    headerName: 'Share price',
    width: 130,
    align: 'right',
    sortable: false,
    contextualHelp: <SharePriceContent />,
  },
  {
    field: AssetValuationTableColumns.TAX_SITUS,
    headerName: 'State',
    width: 180,
    sortable: false,
    renderHeader: () => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <HeaderCellTypography sx={{ pl: 1, py: 2.5 }}>
          State
        </HeaderCellTypography>
        <ContextualHelpTooltip>
          <PopperContent body={STATE_TAX_SITUS_HELP_BODY} />
        </ContextualHelpTooltip>
      </Box>
    ),
  },
  {
    field: AssetValuationTableColumns.TOTAL_MARKET_VALUE,
    headerName: 'Total market value',
    width: 140,
    sortable: false,
    align: 'right',
  },
];

interface GetRowsOptions {
  hideValues: boolean;
  isIntegratedValuation: boolean;
  onSitusChange?: (
    asset: AssetV2Fragment,
    nextSitus: AssetTaxSitusFormValue
  ) => void | Promise<void>;
  ownerPrincipalIds?: string[];
}

const getRows = (
  allAssets: AssetV2Extended[],
  opts: GetRowsOptions = { hideValues: false, isIntegratedValuation: false }
) => {
  const assets = opts.isIntegratedValuation
    ? allAssets.slice(0, NUMBER_PREVIEW_ASSETS)
    : allAssets;

  const renderTaxSitusCell = (asset: AssetV2Extended) => {
    if (!opts.onSitusChange) return EMPTY_CONTENT_HYPHEN;
    return (
      <IntegratedAssetTaxSitusCell
        asset={asset}
        onChange={opts.onSitusChange}
        ownerPrincipalIds={opts.ownerPrincipalIds}
      />
    );
  };

  const rows = (() => {
    if (opts.hideValues) {
      return assets.map((asset) => ({
        id: asset.id,
        [AssetValuationTableColumns.DISPLAY_NAME]: getAssetCellDefinition({
          displayName: asset.displayName ?? '',
          displayCategory: asset.displayCategory,
          accountName: asset.accountName,
        }),
        [AssetValuationTableColumns.SHARE_COUNT]: EMPTY_CONTENT_HYPHEN,
        [AssetValuationTableColumns.SHARE_VALUE]: EMPTY_CONTENT_HYPHEN,
        [AssetValuationTableColumns.TAX_SITUS]: renderTaxSitusCell(asset),
        [AssetValuationTableColumns.TOTAL_MARKET_VALUE]: EMPTY_CONTENT_HYPHEN,
      }));
    }

    return assets.map((asset, i) => ({
      id: asset.id || i,
      [AssetValuationTableColumns.DISPLAY_NAME]: getAssetCellDefinition({
        displayName: asset.displayName ?? 'Unknown',
        displayCategory: asset.displayCategory,
        accountName: asset.accountName,
      }),
      [AssetValuationTableColumns.SHARE_COUNT]:
        asset.assetValue?.ownershipType ===
          AssetValueV2OwnershipType.ShareBased && asset.assetValue?.shareCount
          ? commaDecimal(asset.assetValue?.shareCount ?? new Decimal(0))
          : '',
      [AssetValuationTableColumns.SHARE_VALUE]:
        (asset.assetValue?.ownershipType ===
          AssetValueV2OwnershipType.ShareBased &&
          formatCurrency(asset.assetValue?.shareValue ?? new Decimal(0))) ??
        '',
      [AssetValuationTableColumns.TAX_SITUS]: renderTaxSitusCell(asset),
      [AssetValuationTableColumns.TOTAL_MARKET_VALUE]: formatCurrency(
        asset.assetValue?.value ?? new Decimal(0)
      ),
    }));
  })();

  return { rows, totalCount: allAssets.length };
};

type RowType = ReturnType<typeof getRows>['rows'][number];

export type AssetValuationDisplayTableVariant = 'full' | 'compact';

export interface AssetValuationDisplayTableProps {
  integrationEntities?: AssetIntegration_IntegrationEntityFragment[];
  assets: AssetV2Extended[];
  variant?: AssetValuationDisplayTableVariant;
  hideValues?: boolean;
  shouldShowSumRow?: boolean;
  printOnly?: boolean;
  // Omit to hide the State column.
  onSitusChange?: (
    asset: AssetV2Fragment,
    nextSitus: AssetTaxSitusFormValue
  ) => void | Promise<void>;
  // Host entity's principal client IDs, used to drive the owner-aware
  // default displayed in the State cell when an asset has no persisted
  // situs. Has no effect unless `onSitusChange` is also provided.
  ownerPrincipalIds?: string[];
}

export default function AssetValuationDisplayTable({
  assets,
  hideValues,
  shouldShowSumRow,
  variant,
  integrationEntities,
  printOnly,
  onSitusChange,
  ownerPrincipalIds,
}: AssetValuationDisplayTableProps) {
  const isIntegratedValuation = !isEmpty(integrationEntities);
  const { rows, totalCount } = getRows(assets, {
    hideValues: Boolean(hideValues),
    isIntegratedValuation,
    onSitusChange,
    ownerPrincipalIds,
  });
  const sum =
    assets.reduce((acc, asset) => {
      return acc.plus(asset.assetValue?.value ?? new Decimal(0));
    }, new Decimal(0)) ?? new Decimal(0);

  const renderedColumns = useMemo(() => {
    return columns.filter((colDef) => {
      const field = colDef.field as AssetValuationTableColumns;
      if (field === AssetValuationTableColumns.TAX_SITUS && !onSitusChange) {
        return false;
      }
      if (variant === 'compact') {
        // we only want to show asset name, tax situs, and total market value in this scenario
        if (
          field === AssetValuationTableColumns.SHARE_COUNT ||
          field === AssetValuationTableColumns.SHARE_VALUE
        ) {
          return false;
        }
      }

      return colDef;
    });
  }, [variant, onSitusChange]);

  return (
    <Box>
      <DataTable_LEGACY
        rowHoverVariant="transparent"
        sx={{
          '& .MuiDataGrid-row': {
            backgroundColor: 'transparent',
          },
        }}
        rows={rows}
        columns={renderedColumns}
        variant="long"
        hideFooter_DANGEROUS_ONLY_GOES_TO_100_ITEMS
        disableVirtualization={printOnly}
      />
      {shouldShowSumRow && variant === 'full' && (
        <>
          <Box
            sx={{ borderBottom: `solid ${COLORS.NAVY[600]} 2px` }}
            pb={0.5}
          />
          <Stack flexDirection="row" justifyContent="space-between" my={2}>
            <Typography variant="title-s">Total value</Typography>
            <Typography variant="title-l">{formatCurrency(sum)}</Typography>
          </Stack>
        </>
      )}
      <IntegratedAssetValuationDisplayTableFooter
        integrationEntities={integrationEntities}
        printOnly={printOnly}
        rows={rows}
        totalRowCount={totalCount}
      />
    </Box>
  );
}

interface IntegratedAssetValuationDisplayTableFooterProps {
  totalRowCount: number;
  integrationEntities?: AssetIntegration_IntegrationEntityFragment[];
  printOnly?: boolean;
  rows: RowType[];
}

function IntegratedAssetValuationDisplayTableFooter({
  integrationEntities,
  rows,
  printOnly,
  totalRowCount,
}: IntegratedAssetValuationDisplayTableFooterProps) {
  if (isEmpty(integrationEntities) || totalRowCount <= rows.length) {
    return null;
  }

  return (
    <Box mt={1}>
      {/* if we can, we want to link users out to their addepar instance to explore the entity more */}
      {!isEmpty(integrationEntities) && !printOnly ? (
        <Stack spacing={0.5}>
          {integrationEntities?.map((integrationEntity) => (
            <ExternalIntegrationButton
              key={integrationEntity.id}
              integrationEntity={integrationEntity}
            />
          ))}
        </Stack>
      ) : (
        <Typography
          component="strong"
          display="block"
          textAlign="center"
          variant="body-s"
        >
          ...and {totalRowCount - rows.length} other assets
        </Typography>
      )}
    </Box>
  );
}
